import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [activeLink, setActiveLink] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = (link) => {
    setActiveLink(link);
    if (!isDesktop) {
      setIsMenuOpen(false);
    }
  };

  // Enhanced animation variants
  const navVariants = {
    initial: { y: -100, opacity: 0, scale: 0.95 },
    animate: { 
      y: 0, 
      opacity: 1, 
      scale: 1, 
      transition: { 
        duration: 0.8, 
        ease: "easeOut",
        type: "spring",
        stiffness: 100
      } 
    }
  };

  const linkVariants = {
    initial: { y: 20, opacity: 0, rotateX: -10 },
    animate: { 
      y: 0, 
      opacity: 1, 
      rotateX: 0, 
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    },
    hover: { 
      scale: 1.15, 
      color: '#FFD700', 
      y: -2, 
      transition: { 
        duration: 0.3, 
        type: "spring", 
        stiffness: 200 
      } 
    }
  };

  const mobileMenuVariants = {
    initial: { height: 0, opacity: 0, scaleY: 0.8 },
    animate: { 
      height: 'auto', 
      opacity: 1, 
      scaleY: 1, 
      transition: { 
        duration: 0.5, 
        ease: "easeInOut",
        when: "beforeChildren",
        staggerChildren: 0.1,
        type: "spring",
        stiffness: 120
      } 
    },
    exit: { 
      height: 0, 
      opacity: 0, 
      scaleY: 0.8, 
      transition: { 
        duration: 0.4, 
        ease: "easeInOut" 
      } 
    }
  };

  const mobileLinkVariants = {
    initial: { x: -30, opacity: 0, rotate: -5 },
    animate: { 
      x: 0, 
      opacity: 1, 
      rotate: 0, 
      transition: { 
        duration: 0.4, 
        type: "spring", 
        stiffness: 150 
      } 
    },
    hover: { 
      x: 8, 
      scale: 1.05, 
      transition: { 
        duration: 0.2 
      } 
    }
  };

  // Enhanced navbar styles with gradient and glow
  const navStyle = {
    background: scrolled 
      ? 'linear-gradient(135deg, rgba(11, 110, 79, 0.95), rgba(7, 88, 63, 0.95))' 
      : 'linear-gradient(135deg, #0B6E4F, #0A5C42)',
    color: '#FFFFFF',
    position: 'fixed',
    width: '100%',
    zIndex: 2000,
    boxShadow: scrolled 
      ? '0 4px 20px rgba(0, 255, 127, 0.2)' 
      : '0 4px 6px rgba(0, 255, 127, 0.1)',
    top: 0,
    left: 0,
    backdropFilter: scrolled ? 'blur(8px)' : 'none',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  };

  // Container styles
  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 1rem',
  };

  // Flex container styles
  const flexStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: scrolled ? '3.5rem' : '4rem',
  };

  // Enhanced logo container with glow effect
  const logoContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.3))'
  };

  // Logo image styles with bounce animation
  const logoImageStyle = {
    height: scrolled ? '28px' : '32px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2)) brightness(1.1)',
    transform: 'perspective(500px) translateZ(0)',
  };

  // Logo text styles with gradient text
  const logoTextStyle = {
    fontSize: scrolled ? '1.25rem' : '1.5rem',
    fontWeight: '700',
    fontFamily: 'Poppins, sans-serif',
    textDecoration: 'none',
    background: 'linear-gradient(45deg, #FFFFFF, #FFD700)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
  };

  // Desktop menu styles
  const desktopMenuStyle = {
    display: isDesktop ? 'flex' : 'none',
    alignItems: 'center',
    gap: '0.5rem',
  };

  // Enhanced navigation link styles with underline animation
  const getNavLinkStyle = (link) => ({
    color: activeLink === link ? '#FFD700' : '#FFFFFF',
    textDecoration: 'none',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '0.9rem',
    fontWeight: '500',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.375rem',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    background: activeLink === link 
      ? 'linear-gradient(0deg, rgba(255,215,0,0.1), transparent)' 
      : 'none',
    ':after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '2px',
      backgroundColor: '#FFD700',
      transform: activeLink === link ? 'scaleX(1)' : 'scaleX(0)',
      transformOrigin: 'bottom right',
      transition: 'transform 0.3s ease-out'
    }
  });

  // Enhanced CTA button with pulse effect
  const ctaButtonStyle = {
    background: activeLink === 'contact' 
      ? 'linear-gradient(45deg, #FFC300, #FFD700)' 
      : 'linear-gradient(45deg, #FFD700, #FFE866)',
    color: '#0B6E4F',
    padding: '0.5rem 1rem',
    fontWeight: '600',
    borderRadius: '50px',
    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)',
    position: 'relative',
    overflow: 'hidden',
    animation: 'pulse 2s infinite'
  };

  // Mobile menu button styles
  const mobileButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: isDesktop ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    borderRadius: '50%',
    // eslint-disable-next-line no-dupe-keys
    background: 'rgba(255, 255, 255, 0.1)',
  };

  // Mobile menu styles with gradient
  const mobileMenuStyle = {
    background: 'linear-gradient(180deg, #0B6E4F, #0A5C42)',
    width: '100%',
    position: 'absolute',
    left: 0,
    top: scrolled ? '3.5rem' : '4rem',
    boxShadow: '0 10px 25px rgba(0, 255, 127, 0.2)',
    overflow: 'hidden',
  };

  // Enhanced mobile link styles
  const getMobileLinkStyle = (link) => ({
    display: 'block',
    padding: '0.75rem 1.5rem',
    color: activeLink === link ? '#FFD700' : '#FFFFFF',
    textDecoration: 'none',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1rem',
    fontWeight: '500',
    position: 'relative',
    transition: 'all 0.3s ease',
    background: activeLink === link 
      ? 'rgba(255,215,0,0.1)' 
      : 'transparent',
    ':before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '4px',
      backgroundColor: '#FFD700',
      transform: activeLink === link ? 'scaleY(1)' : 'scaleY(0)',
      transition: 'transform 0.3s ease'
    }
  });

  // Mobile CTA styles
  const mobileCtaStyle = {
    background: activeLink === 'contact' 
      ? 'linear-gradient(45deg, #FFC300, #FFD700)' 
      : 'linear-gradient(45deg, #FFD700, #FFE866)',
    color: '#0B6E4F',
    fontWeight: '600',
    margin: '0.75rem 1.5rem',
    padding: '0.75rem 1.5rem',
    borderRadius: '50px',
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)',
    display: 'block',
    position: 'relative',
    animation: 'pulse 2s infinite'
  };

  return (
    <motion.nav 
      style={navStyle}
      variants={navVariants}
      initial="initial"
      animate="animate"
    >
      <div style={containerStyle}>
        <div style={flexStyle}>
          <motion.div 
            style={logoContainerStyle}
            whileHover={{ scale: 1.1, rotate: 2 }}
            transition={{ duration: 0.4, type: "spring", stiffness: 150 }}
          >
            <motion.img 
              src="/WhatsApp Image 2025-05-02 at 23.07.56_080bf69b.jpg"
              alt="AIC Logo" 
              style={logoImageStyle}
              initial={{ rotate: -20, opacity: 0, scale: 0.8 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, type: "spring", stiffness: 120 }}
              whileHover={{ rotate: 5, scale: 1.2 }}
            />
            <motion.a 
              href="/" 
              style={logoTextStyle} 
              onClick={() => handleLinkClick('home')}
              whileHover={{ y: -4, scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              AIC
            </motion.a>
          </motion.div>
          
          <div style={desktopMenuStyle}>
            {['home', 'about', 'how-it-works', 'investments', 'membership'].map((link, index) => (
              <motion.a
                key={link}
                href={`#${link}`}
                style={getNavLinkStyle(link)}
                onClick={() => handleLinkClick(link)}
                variants={linkVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                custom={index}
                transition={{ delay: 0.1 * index }}
              >
                {link.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </motion.a>
            ))}
            <motion.a 
              href="#contact" 
              style={ctaButtonStyle} 
              onClick={() => handleLinkClick('contact')}
              whileHover={{ scale: 1.15, boxShadow: '0 8px 25px rgba(255, 195, 0, 0.5)' }}
              whileTap={{ scale: 0.9 }}
            >
              Contact
            </motion.a>
          </div>
          
          <motion.button 
            onClick={toggleMenu} 
            style={mobileButtonStyle}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            whileHover={{ rotate: 180, scale: 1.2 }}
            whileTap={{ scale: 0.8 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <motion.svg 
              style={{ 
                width: '1.5rem',
                height: '1.5rem',
                fill: 'none', 
                stroke: isMenuOpen ? '#FFD700' : '#FFFFFF',
                strokeWidth: '2',
                strokeLinecap: 'round',
              }} 
              viewBox="0 0 24 24"
              animate={{ stroke: isMenuOpen ? '#FFD700' : '#FFFFFF' }}
            >
              <motion.path 
                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                initial={{ pathLength: 0, rotate: -45 }}
                animate={{ pathLength: 1, rotate: 0 }}
                transition={{ duration: 0.4, type: "spring" }}
              />
            </motion.svg>
          </motion.button>
        </div>
      </div>
      
      <AnimatePresence>
        {isMenuOpen && !isDesktop && (
          <motion.div 
            style={mobileMenuStyle}
            variants={mobileMenuVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {['home', 'about', 'how-it-works', 'investments', 'membership'].map((link) => (
              <motion.a
                key={link}
                href={`#${link}`}
                style={getMobileLinkStyle(link)}
                onClick={() => handleLinkClick(link)}
                variants={mobileLinkVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
              >
                {link.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </motion.a>
            ))}
            <motion.a 
              href="#contact" 
              style={mobileCtaStyle} 
              onClick={() => handleLinkClick('contact')}
              variants={mobileLinkVariants}
              initial="initial"
              animate="animate"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              Contact
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

export default Navigation;

// Inline CSS for pulse animation
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4); }
    50% { transform: scale(1.05); box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6); }
    100% { transform: scale(1); box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4); }
  }
`;
document.head.appendChild(style);